import { create, tsx } from '@dojo/framework/core/vdom';

import * as css from './ClippedImageSummary.m.css';
import { RenderResult } from '@dojo/framework/core/interfaces';
import Text from './Text';

interface ClippedImageSummaryProperties {
	/** Responsive behavior - default: hide-medium  */
	collapse?:
		| 'hide-large'
		| 'hide-medium'
		| 'hide-small'
		| 'stack-large'
		| 'stack-medium'
		| 'stack-small'
		| 'stack-reverse-large'
		| 'stack-reverse-medium'
		| 'stack-reverse-small';
	/** The ratio size of the content section, default 0.56 - value must between 0 and 1 **/
	contentRatio?: number;
	/** Reverses the content and image sections */
	reverse?: boolean;
}

interface ClippedImageSummaryChildren {
	title: RenderResult;
	blurb: RenderResult;
	button?: RenderResult;
	images: {
		main: RenderResult;
		top?: RenderResult;
		bottom?: {
			left: RenderResult;
			right: RenderResult;
		};
	};
}

const factory = create()
	.properties<ClippedImageSummaryProperties>()
	.children<ClippedImageSummaryChildren>();

const ClippedImageSummary = factory(function ClippedImageSummary({ properties, children }) {
	const { contentRatio = 0.56, reverse = false, collapse = 'hide-medium' } = properties();
	const [
		{
			title,
			blurb,
			button,
			images: { main, top, bottom }
		}
	] = children();

	let collapseClass = '';
	switch (collapse) {
		case 'hide-small':
			collapseClass = css.hideSmall;
			break;
		case 'hide-medium':
			collapseClass = css.hideMedium;
			break;
		case 'hide-large':
			collapseClass = css.hideLarge;
			break;
		case 'stack-small':
			collapseClass = css.stackSmall;
			break;
		case 'stack-medium':
			collapseClass = css.stackMedium;
			break;
		case 'stack-large':
			collapseClass = css.stackLarge;
			break;
		case 'stack-reverse-small':
			collapseClass = css.stackSmallReverse;
			break;
		case 'stack-reverse-medium':
			collapseClass = css.stackMediumReverse;
			break;
		case 'stack-reverse-large':
			collapseClass = css.stackLargeReverse;
			break;
		default:
			break;
	}

	const titleNode = typeof title === 'string' ? <Text type="section">{title}</Text> : title;
	const blurbNode = typeof blurb === 'string' ? <Text type="body">{blurb}</Text> : blurb;

	const images = (
		<virtual>
			{top && (
				<div classes={[css.topImageContainer]}>
					<div classes={[css.imageWrapper]}>{top}</div>
				</div>
			)}
			<div classes={[css.mainImageContainer, collapseClass, reverse && css.reverse]}>
				<div classes={[css.imageWrapper]}>{main}</div>
			</div>
			{bottom && (
				<div classes={[css.bottomImageContainer]}>
					<div classes={[css.imageWrapper]}>{bottom.left}</div>
					<div classes={[css.imageWrapper]}>{bottom.right}</div>
				</div>
			)}
		</virtual>
	);

	const summary = (
		<div classes={[css.content, reverse && css.reverse, collapseClass]}>
			<div>{titleNode}</div>
			<div>{blurbNode}</div>
			{button && <div classes={[css.button]}>{button}</div>}
		</div>
	);

	return (
		<div
			style={`--content-size-ratio: ${contentRatio};`}
			classes={[css.root, collapseClass, reverse && css.reverse]}
		>
			<div classes={[css.left, collapseClass, reverse && css.reverse]}>
				{reverse ? images : summary}
			</div>
			<div classes={[css.right, collapseClass, reverse && css.reverse]}>
				{reverse ? summary : images}
			</div>
		</div>
	);
});

export default ClippedImageSummary;
